import { useSessionTokenGetter } from '@develio/auth/client';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { type StepForm, WizardField } from '../WizardField';

function ProfileLockedDisplay({
  form,
  name,
  label,
}: { form: StepForm; name: string; label: string }) {
  const value = form.watch(name) as string | undefined;
  return (
    <div>
      <div className="flex items-center gap-1.5">
        <p className="text-sm font-medium text-slate-700">{label}</p>
        <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-500">
          from EDC profile
        </span>
      </div>
      <div className="mt-1 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
        {value || <span className="text-slate-400 italic">Not set in jurisdiction profile</span>}
      </div>
    </div>
  );
}

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';

interface Props {
  form: StepForm;
}

const PROJECT_TYPE_OPTIONS = [
  { value: 'new_startup', label: 'New startup / greenfield' },
  { value: 'expansion', label: 'Expansion of existing business' },
  { value: 'retention', label: 'Business retention' },
] as const;

const STATE_OPTIONS = [
  { value: 'TX', label: 'Texas' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CO', label: 'Colorado' },
  { value: 'KS', label: 'Kansas' },
] as const;

interface RimsDataset {
  id: string;
  regionName: string;
  multiplierType: string;
  ioVintageYear: number | null;
  regionalVintageYear: number | null;
  status: 'uploaded' | 'parsing' | 'ready' | 'failed';
}

interface Industry {
  beaIoCode: string;
  industryName: string;
}

export function Step1ProjectDescription({ form }: Props) {
  const projectType = form.watch('step1.projectType') as
    | 'new_startup'
    | 'expansion'
    | 'retention'
    | undefined;

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold text-slate-800">Project Description</h3>
        <p className="mt-0.5 text-sm text-slate-500">
          Basic information about the project you are evaluating.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <WizardField
            form={form}
            name="step1.projectName"
            label="Project name"
            placeholder="e.g. Acme Manufacturing Expansion"
          />
        </div>

        <WizardField
          form={form}
          name="step1.projectType"
          label="Project type"
          type="select"
          options={PROJECT_TYPE_OPTIONS}
        />

        <div />

        <div className="col-span-2">
          <IndustryPicker form={form} />
        </div>

        <WizardField form={form} name="step1.city" label="City" placeholder="e.g. Amarillo" />

        <WizardField form={form} name="step1.county" label="County" placeholder="e.g. Potter" />

        <WizardField
          form={form}
          name="step1.state"
          label="State"
          type="select"
          options={STATE_OPTIONS}
        />

        <WizardField
          form={form}
          name="step1.analysisPeriodYears"
          label="Analysis period (years)"
          type="number"
          min={1}
          max={30}
          hint="Usually 10"
        />

        <ProfileLockedDisplay form={form} name="step1.schoolDistrict" label="School district" />
        <ProfileLockedDisplay form={form} name="step1.specialDistrict" label="Special district" />
      </div>

      {projectType === 'retention' && <CounterfactualPanel form={form} />}
    </div>
  );
}

/**
 * ADR-031 Part A — retention counterfactual inputs. Rendered only when
 * `projectType === 'retention'`. The four knobs describe what would happen
 * absent the incentive: probability of departure, when, how much stays on
 * the tax roll anyway, and how much the local economy reabsorbs.
 *
 * Evidence textarea is always shown but only REQUIRED at submission when
 * `probabilityOfDeparture > 0.5` (enforced server-side by
 * isRetentionEvidenceMissing → 400 RETENTION_EVIDENCE_REQUIRED; the copy
 * below mirrors that rule so analysts don't hit a server round-trip to
 * discover it).
 */
function CounterfactualPanel({ form }: { form: StepForm }) {
  const p = form.watch('retentionCounterfactual.probabilityOfDeparture') as number | undefined;
  const evidenceRequired = typeof p === 'number' && p > 0.5;

  return (
    <div className="rounded-lg border-2 border-amber-300 bg-amber-50/60 p-4">
      <div className="mb-3 flex items-start gap-2">
        <div className="mt-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-amber-500 text-[11px] font-bold text-white">
          !
        </div>
        <div>
          <h4 className="text-sm font-semibold text-amber-900">Retention counterfactual</h4>
          <p className="mt-0.5 text-xs text-amber-800">
            A retention analysis measures <strong>loss avoided</strong>, not gains created. These
            four inputs describe what would happen <em>absent</em> the incentive. They drive every
            fiscal number in the report — treat them with the same rigor as a financial projection.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <WizardField
          form={form}
          name="retentionCounterfactual.probabilityOfDeparture"
          label="Probability of departure"
          format="percent"
          min={0}
          max={1}
          step={0.01}
          hint="How likely is the firm to leave without the incentive? Evidence required > 50%."
        />
        <WizardField
          form={form}
          name="retentionCounterfactual.departureTimingYear"
          label="Departure year"
          type="number"
          min={1}
          max={10}
          hint="Year in which departure would occur (1 = immediately, 10 = end of horizon)."
        />
        <WizardField
          form={form}
          name="retentionCounterfactual.retainedSharePostDeparture"
          label="Retained share post-departure"
          format="percent"
          min={0}
          max={1}
          step={0.01}
          hint="Share of real property / inventory that stays on the tax roll anyway."
        />
        <WizardField
          form={form}
          name="retentionCounterfactual.localAbsorptionRate"
          label="Local absorption rate"
          format="percent"
          min={0}
          max={1}
          step={0.01}
          hint="Share of jobs reabsorbed by other local employers within 3 years."
        />
      </div>

      <div className="mt-4">
        <div className="flex items-center justify-between gap-2">
          <label
            htmlFor="retentionCounterfactual.evidence"
            className="block text-sm font-medium text-slate-700"
          >
            Evidence{' '}
            {evidenceRequired && (
              <span className="ml-1 rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-red-700">
                Required
              </span>
            )}
          </label>
        </div>
        <textarea
          id="retentionCounterfactual.evidence"
          rows={3}
          maxLength={2000}
          placeholder="e.g., Relocation quote from Oklahoma EDC dated 2026-02-14; board resolution authorizing site search; LOI from competing jurisdiction."
          className={`mt-1 block w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-500 ${
            evidenceRequired ? 'border-amber-400 bg-white' : 'border-slate-300 bg-white'
          }`}
          {...form.register('retentionCounterfactual.evidence')}
        />
        <p className="mt-1 text-xs text-slate-500">
          {evidenceRequired
            ? 'Required when probability of departure exceeds 50%. LOI, board resolution, relocation quote, or equivalent.'
            : 'Optional but strongly recommended. Required when probability of departure exceeds 50%.'}
        </p>
      </div>
    </div>
  );
}

/**
 * Dataset + industry picker backed by the org's uploaded RIMS II datasets.
 *
 * Writes three form fields on selection:
 *   - step1.rimsDatasetId (which dataset to source multipliers from)
 *   - step1.naicsCode     (the chosen industry's BEA I/O code, 6 chars)
 *   - step1.industryDescription (the industry's display name)
 *
 * Graceful degradation: if no ready datasets exist, falls back to free-text
 * NAICS + industry description inputs and points the user to Settings.
 */
function IndustryPicker({ form }: { form: StepForm }) {
  const getToken = useSessionTokenGetter();
  const [datasets, setDatasets] = useState<RimsDataset[] | null>(null);
  const [industries, setIndustries] = useState<Industry[] | null>(null);
  const [loadingIndustries, setLoadingIndustries] = useState(false);

  // Searchable combobox state (replaces a 377-option native select).
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);

  const selectedDatasetId = form.watch('step1.rimsDatasetId') as string | undefined;
  const selectedNaicsCode = form.watch('step1.naicsCode') as string | undefined;

  // Fetch datasets on mount. The effect guards against repeated defaulting
  // by checking the current form value at run time rather than via closure.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${API_URL}/api/v1/rims-datasets`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          if (!cancelled) setDatasets([]);
          return;
        }
        const body = (await res.json()) as { datasets: RimsDataset[] };
        if (cancelled) return;
        // Only datasets that have finished parsing are usable.
        const ready = (body.datasets ?? []).filter((d) => d.status === 'ready');
        setDatasets(ready);
        // If user hasn't chosen yet, default to most recent ready dataset.
        const currentChoice = form.getValues('step1.rimsDatasetId') as string | undefined;
        if (!currentChoice && ready.length > 0) {
          form.setValue('step1.rimsDatasetId', ready[ready.length - 1]?.id);
        }
      } catch {
        if (!cancelled) setDatasets([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken, form]);

  // Fetch industries whenever the selected dataset changes.
  useEffect(() => {
    if (!selectedDatasetId) {
      setIndustries(null);
      return;
    }
    let cancelled = false;
    setLoadingIndustries(true);
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${API_URL}/api/v1/rims-datasets/${selectedDatasetId}/industries`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          if (!cancelled) setIndustries([]);
          return;
        }
        const body = (await res.json()) as { industries: Industry[] };
        if (!cancelled) setIndustries(body.industries ?? []);
      } catch {
        if (!cancelled) setIndustries([]);
      } finally {
        if (!cancelled) setLoadingIndustries(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedDatasetId, getToken]);

  // Keep the search box label in sync when a code is already selected
  // (re-run/edit flow) or once the industry list finishes loading.
  useEffect(() => {
    if (!selectedNaicsCode || !industries) return;
    const match = industries.find((i) => i.beaIoCode === selectedNaicsCode);
    if (match) setQuery((q) => (q ? q : match.industryName));
  }, [selectedNaicsCode, industries]);

  // Filter by industry name or code; cap the rendered list for performance.
  const MAX_RESULTS = 50;
  const filtered = useMemo(() => {
    if (!industries) return [];
    const q = query.trim().toLowerCase();
    const matches = q
      ? industries.filter(
          (i) => i.industryName.toLowerCase().includes(q) || i.beaIoCode.toLowerCase().includes(q),
        )
      : industries;
    return matches.slice(0, MAX_RESULTS);
  }, [industries, query]);

  const selectIndustry = useCallback(
    (industry: Industry) => {
      form.setValue('step1.naicsCode', industry.beaIoCode, { shouldValidate: true });
      form.setValue('step1.industryDescription', industry.industryName, { shouldValidate: true });
      setQuery(industry.industryName);
      setOpen(false);
    },
    [form],
  );

  const naicsError = (
    form.formState.errors.step1 as { naicsCode?: { message?: string } } | undefined
  )?.naicsCode?.message;
  const industryError = (
    form.formState.errors.step1 as { industryDescription?: { message?: string } } | undefined
  )?.industryDescription?.message;

  const inputClass = useMemo(
    () =>
      'mt-1 block w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-500 border-slate-300 bg-white',
    [],
  );
  const errorInputClass = useMemo(
    () =>
      'mt-1 block w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-500 border-red-400 bg-red-50',
    [],
  );

  // --- No datasets available: fall back to free-text inputs. ---
  if (datasets && datasets.length === 0) {
    return (
      <div className="rounded-md border border-amber-200 bg-amber-50 p-4">
        <p className="text-sm font-medium text-amber-900">No RIMS II dataset uploaded</p>
        <p className="mt-1 text-xs text-amber-800">
          Without a dataset, the industry picker can't offer guaranteed multiplier matches. You can
          continue with free-text entry, or{' '}
          <Link to="/settings/rims" className="underline">
            upload a dataset first
          </Link>
          .
        </p>
        <div className="mt-4 grid grid-cols-2 gap-4">
          <WizardField
            form={form}
            name="step1.naicsCode"
            label="NAICS / industry code"
            placeholder="e.g. 336111"
            hint="6-digit NAICS or BEA I/O code"
          />
          <WizardField
            form={form}
            name="step1.industryDescription"
            label="Industry description"
            placeholder="e.g. Motor vehicle manufacturing"
          />
        </div>
      </div>
    );
  }

  // --- Loading datasets. ---
  if (datasets === null) {
    return (
      <div className="rounded-md border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
        Loading industry list…
      </div>
    );
  }

  // --- Normal flow: dataset picker (if >1) + industry dropdown. ---
  return (
    <div className="space-y-4">
      {datasets.length > 1 && (
        <div>
          <label htmlFor="step1.rimsDatasetId" className="block text-sm font-medium text-slate-700">
            RIMS II dataset
          </label>
          <select
            id="step1.rimsDatasetId"
            className={inputClass}
            value={selectedDatasetId ?? ''}
            onChange={(e) => form.setValue('step1.rimsDatasetId', e.target.value)}
          >
            {datasets.map((d) => (
              <option key={d.id} value={d.id}>
                {d.regionName} · {d.multiplierType === 'type_ii' ? 'Type II' : d.multiplierType}
                {d.regionalVintageYear ? ` · ${d.regionalVintageYear}` : ''}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-slate-400">
            Region whose multipliers will drive this analysis.
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <label htmlFor="step1.naicsCode" className="block text-sm font-medium text-slate-700">
            Industry
          </label>
          <div className="relative">
            <input
              id="step1.naicsCode"
              type="text"
              role="combobox"
              aria-expanded={open}
              aria-controls="industry-listbox"
              aria-autocomplete="list"
              autoComplete="off"
              className={naicsError ? errorInputClass : inputClass}
              placeholder={loadingIndustries ? 'Loading industries…' : 'Search by industry name…'}
              disabled={loadingIndustries || !industries}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setOpen(true);
                setHighlight(0);
                // Clear any prior selection so a stale code can't ship with new text.
                if (selectedNaicsCode)
                  form.setValue('step1.naicsCode', '', { shouldValidate: true });
              }}
              onFocus={() => setOpen(true)}
              onBlur={() => window.setTimeout(() => setOpen(false), 120)}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setOpen(true);
                  setHighlight((h) => Math.min(h + 1, filtered.length - 1));
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setHighlight((h) => Math.max(h - 1, 0));
                } else if (e.key === 'Enter') {
                  if (open && filtered[highlight]) {
                    e.preventDefault();
                    selectIndustry(filtered[highlight]);
                  }
                } else if (e.key === 'Escape') {
                  setOpen(false);
                }
              }}
            />
            {open && industries && filtered.length > 0 && (
              <ul
                id="industry-listbox"
                className="absolute z-20 mt-1 max-h-72 w-full overflow-auto rounded-md border border-slate-200 bg-white py-1 shadow-lg"
              >
                {filtered.map((i, idx) => (
                  <li key={i.beaIoCode}>
                    <button
                      type="button"
                      className={`flex w-full cursor-pointer items-baseline justify-between gap-2 px-3 py-2 text-left text-sm ${
                        idx === highlight ? 'bg-slate-100' : ''
                      }`}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        selectIndustry(i);
                      }}
                      onMouseEnter={() => setHighlight(idx)}
                    >
                      <span className="text-slate-800">{i.industryName}</span>
                      <span className="shrink-0 text-xs text-slate-400">{i.beaIoCode}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {open && industries && query.trim() && filtered.length === 0 && (
              <div className="absolute z-20 mt-1 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-500 shadow-lg">
                No industries match “{query}”.
              </div>
            )}
          </div>
          {naicsError ? (
            <p className="mt-1 text-xs text-red-600">{naicsError}</p>
          ) : industryError ? (
            <p className="mt-1 text-xs text-red-600">{industryError}</p>
          ) : (
            <p className="mt-1 text-xs text-slate-400">
              Start typing to find the industry that best matches the project. We use it to estimate
              the project&rsquo;s ripple effect on the local economy.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
